<script setup lang="ts">
    import { computed, ref } from 'vue';
    const { $theme } = useNuxtApp();

    interface Props {
        preventDefault?: boolean;
        stopPropagation?: boolean;
        disabled?: boolean;
        loading?: boolean;
        className?: string;
        extraClassName?: string;
        type?: 'button' | 'submit' | 'reset';
    }

    const props = withDefaults(defineProps<Props>(), {
        preventDefault: false,
        stopPropagation: false,
        disabled: false,
        loading: false,
        type: 'button',
    });

    const emit = defineEmits(['clicked']);

    const buttonClassName = computed(() => {
        return `${props.className ?? $theme('button.style.default')} ${props.extraClassName ?? ''}`;
    });

    const submitted = ref(false);

    async function loadUntil<T>(promise: Promise<T>, minimumDuration: number = 0): Promise<T> {
        load();
        const response = await Promise.all([promise, wait(minimumDuration)]).then((values) => values[0]);
        stop();
        return response;
    }

    function load() {
        submitted.value = true;
    }

    function stop() {
        submitted.value = false;
    }

    function clicked(event: Event) {
        if (props.disabled) {
            return;
        }

        const payload: LoadingButtonClickedEventPayload = {
            event,
            loadUntil,
            load,
            stop,
        };
        emit('clicked', payload);

        if (props.stopPropagation && event) {
            event.stopPropagation();
        }

        if (props.preventDefault && event) {
            event.preventDefault();
        }
    }

    const isLoading = computed(() => {
        return props.loading || submitted.value;
    });
    const isDisabled = computed(() => {
        return props.disabled || isLoading.value;
    });

    defineExpose({
        loadUntil,
        load,
        stop,
    });
</script>

<template>
    <button
        :class="[buttonClassName, isDisabled ? 'is-disabled' : '']"
        :disabled="isDisabled"
        :type="type"
        @click="clicked"
    >
        <Loader v-if="isLoading" />
        <slot></slot>
    </button>
</template>

<style scoped>
    .is-disabled {
        @apply opacity-30 pointer-events-none;
    }
</style>
